import * as Sentry from '@sentry/react';
import { API_URL } from './env';

type SentryInitOptions = {
  dsn?: string | null;
  mode?: string;
  debug?: boolean;
};

export function initializeSentry(options: SentryInitOptions = {}): void {
  const envMode = import.meta.env.MODE ?? 'development';
  const envDebug = import.meta.env.VITE_SENTRY_DEBUG;
  const envDsn = import.meta.env.VITE_SENTRY_DSN;

  const hasModeOverride = Object.prototype.hasOwnProperty.call(options, 'mode');
  const hasDebugOverride = Object.prototype.hasOwnProperty.call(options, 'debug');
  const hasDsnOverride = Object.prototype.hasOwnProperty.call(options, 'dsn');

  const mode = hasModeOverride ? options.mode ?? 'development' : envMode;
  const debugEnabled = hasDebugOverride ? options.debug : envDebug;
  const SENTRY_DSN = hasDsnOverride ? options.dsn : envDsn;
  
  if (!SENTRY_DSN) {
    console.log('[SENTRY] DSN not configured. Add VITE_SENTRY_DSN to enable error tracking.');
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: mode,
      integrations: [
        // Session Replay
        Sentry.replayIntegration({
          // Privacy settings
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: false,
          // Network configuration
          networkDetailAllowUrls: [
            'https://api.getvitly.com',
            'https://getvitly.com'
          ],
        }),
        // Browser tracing for performance
        Sentry.browserTracingIntegration(),
      ],
      // Performance monitoring
      tracesSampleRate: mode === 'production' ? 0.1 : 1.0,
      // Session Replay sampling rates
      replaysSessionSampleRate: mode === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,
      // Trace propagation for distributed tracing (backend CORS updated to allow headers)
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/api\.getvitly\.com/,
        /^https:\/\/.*\.railway\.app/,
        API_URL
      ].filter(Boolean),
      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      
      beforeSend(event) {
        // Filter out sensitive data
        if (event.request) {
          // Remove authorization headers
          if (event.request.headers) {
            delete event.request.headers.authorization;
            delete event.request.headers.cookie;
          }
        }
        
        // Don't send events in development unless explicitly enabled
        if (mode === 'development' && !debugEnabled) {
          console.log('Sentry event (dev mode):', event);
          return null;
        }
        
        return event;
      },
      
      ignoreErrors: [
        // Ignore common browser errors
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        // Ignore network errors that are expected
        'NetworkError',
        'Network request failed',
        // Ignore rate limit errors (handled by app)
        'Too Many Requests',
        'Rate limit exceeded',
      ],
    });

    console.log('[SENTRY] Initialized with Session Replay', {
      sessionSampleRate: mode === 'production' ? '10%' : '100%',
      errorSampleRate: '100%',
      environment: mode
    });
  } catch (error) {
    console.error('[SENTRY] Failed to initialize:', error);
  }
}

// Helper to capture custom events
export function captureEvent(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  extra?: Record<string, any>
): void {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.log(`[Sentry] Event: ${message}`, { level, extra });
    return;
  }
  
  Sentry.captureMessage(message, {
    level,
    extra,
  });
}

// Helper to capture exceptions with context
export function captureException(
  error: Error,
  context?: Record<string, any>
): void {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.error('[Sentry] Exception:', error, context);
    return;
  }
  
  Sentry.captureException(error, {
    extra: context,
  });
}

// Helper to add user context
export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
  subscription?: string;
}): void {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.log('[Sentry] User context:', user);
    return;
  }
  
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
    subscription_plan: user.subscription,
  });
}

// Helper to clear user context on logout
export function clearUserContext(): void {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.log('[Sentry] User context cleared');
    return;
  }
  
  Sentry.setUser(null);
}

// Helper to add breadcrumb
export function addBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, any>
): void {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.log('[Sentry] Breadcrumb:', { message, category, level, data });
    return;
  }
  
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

// Performance monitoring helpers
export function startTransaction(name: string, op: string): any {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.log('[Sentry] Transaction started:', { name, op });
    return null;
  }

  return Sentry.startSpan({
    name,
    op,
  }, () => {
    // Transaction logic here
  });
}

// NOTE: For React-specific Sentry features like withProfiler and ErrorBoundary,
// import them directly from '@sentry/react' in your components instead of from this file.
// This avoids circular dependencies between Sentry and React during module initialization.
